import { FloatingPortal } from '@floating-ui/react'
import { CaretDownIcon } from '@phosphor-icons/react'
import { getMonth, getYear } from 'date-fns'
import { useCallback, useMemo, useState } from 'react'

import { Icon } from '@/components/ui/icon'
import { DAY_NAMES_NARROW, MONTH_NAMES_LONG } from '@/constants/date'
import { DateRange } from '@/models/helpers'

import { PERIOD_OPTIONS, YEARS_RANGE } from './date-picker.constants'
import { useCalendarDays, useHeaderDropdown } from './date-picker.hooks'
import * as S from './date-picker.styled'
import {
  DatePickerProps,
  DatePickerRangeProps,
  DatePickerSingleProps,
  PeriodOption,
} from './date-picker.types'
import { getInitialMonth, getToday } from './date-picker.utils'

export const DatePicker = ({
  value,
  minDate,
  maxDate,
  timezone,
  mode = 'single',
  ...handlers
}: DatePickerProps) => {
  const [currentMonth, setCurrentMonth] = useState(getInitialMonth(value))
  const [rangeStart, setRangeStart] = useState<Date | null>(null)

  const monthDropdown = useHeaderDropdown('bottom-start')
  const yearDropdown = useHeaderDropdown()
  const periodDropdown = useHeaderDropdown()

  // Note: Generate year range based on minDate and maxDate
  const yearRange = useMemo(() => {
    const currentYear = getYear(getToday(timezone))
    const minYear = minDate ? getYear(minDate) : currentYear - YEARS_RANGE
    const maxYear = maxDate ? getYear(maxDate) : currentYear + YEARS_RANGE

    const years: number[] = []
    for (let year = minYear; year <= maxYear; year++) {
      years.push(year)
    }
    return years
  }, [minDate, maxDate, timezone])

  // Note: Get calendar data using custom hook
  const calendarDays = useCalendarDays({
    currentMonth,
    maxDate,
    minDate,
    rangeStart,
    timezone,
    value,
  })

  // Note: Scroll selected year into view when dropdown opens — scrolls only the dropdown
  // container to avoid scrollIntoView propagating to the page scroll position
  const scrollSelectedYearIntoView = useCallback((node: HTMLButtonElement | null) => {
    if (!node) return
    const container = node.parentElement
    if (!container) return
    const nodeRect = node.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()
    container.scrollTop +=
      nodeRect.top - containerRect.top - (containerRect.height - nodeRect.height) / 2
  }, [])

  const handleMonthChange = (monthIndex: number) => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev)
      newDate.setMonth(monthIndex)
      return newDate
    })
    monthDropdown.setIsOpen(false)
  }

  const handleYearChange = (year: number) => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev)
      newDate.setFullYear(year)
      return newDate
    })
    yearDropdown.setIsOpen(false)
  }

  const periodOptions =
    mode === 'range' ? ((handlers as DatePickerRangeProps).periodOptions ?? PERIOD_OPTIONS) : []

  const handlePeriodSelect = (period: PeriodOption) => {
    if (mode !== 'range') return

    const { onRangeChange } = handlers as DatePickerRangeProps
    const range = period.getValue(timezone)
    setRangeStart(null)

    // Note: Navigate calendar to the start date of the selected period
    if (range[0]) {
      setCurrentMonth(new Date(range[0]))
    }

    onRangeChange?.(range)
    periodDropdown.setIsOpen(false)
  }

  const handleDateClick = (date: Date, isDisabled: boolean) => {
    if (isDisabled) return

    if (mode === 'single') {
      const { onChange } = handlers as DatePickerSingleProps
      onChange?.(date)
      return
    }

    const { onRangeChange } = handlers as DatePickerRangeProps
    if (!rangeStart) {
      // Note: First click - set start date and notify with partial range
      setRangeStart(date)
      onRangeChange?.([date, null])
    } else {
      // Note: Second click - set end date and create range
      const range: DateRange = date < rangeStart ? [date, rangeStart] : [rangeStart, date]
      setRangeStart(null)
      onRangeChange?.(range)
    }
  }

  return (
    <S.Container>
      <S.Header>
        <S.SelectTrigger
          ref={monthDropdown.refs.setReference}
          $isOpen={monthDropdown.isOpen}
          type="button"
          {...monthDropdown.getReferenceProps()}
        >
          <Icon icon={CaretDownIcon} />
          {MONTH_NAMES_LONG[getMonth(currentMonth)]}
        </S.SelectTrigger>
        {monthDropdown.isOpen && (
          <FloatingPortal>
            <S.SelectDropdown
              ref={monthDropdown.refs.setFloating}
              style={monthDropdown.floatingStyles}
              {...monthDropdown.getFloatingProps()}
            >
              {MONTH_NAMES_LONG.map((month, index) => (
                <S.SelectOption
                  key={month}
                  $isSelected={index === getMonth(currentMonth)}
                  type="button"
                  onClick={() => handleMonthChange(index)}
                >
                  {month}
                </S.SelectOption>
              ))}
            </S.SelectDropdown>
          </FloatingPortal>
        )}

        <S.SelectContainer>
          <S.SelectTrigger
            ref={yearDropdown.refs.setReference}
            $isOpen={yearDropdown.isOpen}
            type="button"
            {...yearDropdown.getReferenceProps()}
          >
            {getYear(currentMonth)}
            <Icon icon={CaretDownIcon} />
          </S.SelectTrigger>
          {yearDropdown.isOpen && (
            <FloatingPortal>
              <S.SelectDropdown
                ref={yearDropdown.refs.setFloating}
                style={yearDropdown.floatingStyles}
                {...yearDropdown.getFloatingProps()}
              >
                {yearRange.map((year) => {
                  const isSelected = year === getYear(currentMonth)
                  return (
                    <S.SelectOption
                      key={year}
                      ref={isSelected ? scrollSelectedYearIntoView : undefined}
                      $isSelected={isSelected}
                      type="button"
                      onClick={() => handleYearChange(year)}
                    >
                      {year}
                    </S.SelectOption>
                  )
                })}
              </S.SelectDropdown>
            </FloatingPortal>
          )}

          {periodOptions.length > 0 && (
            <>
              <S.PeriodButton
                ref={periodDropdown.refs.setReference}
                $isOpen={periodDropdown.isOpen}
                type="button"
                {...periodDropdown.getReferenceProps()}
              >
                Period
              </S.PeriodButton>
              {periodDropdown.isOpen && (
                <FloatingPortal>
                  <S.SelectDropdown
                    ref={periodDropdown.refs.setFloating}
                    style={periodDropdown.floatingStyles}
                    {...periodDropdown.getFloatingProps()}
                  >
                    {periodOptions.map((period) => (
                      <S.SelectOption
                        key={period.label}
                        $isSelected={false}
                        type="button"
                        onClick={() => handlePeriodSelect(period)}
                      >
                        {period.label}
                      </S.SelectOption>
                    ))}
                  </S.SelectDropdown>
                </FloatingPortal>
              )}
            </>
          )}
        </S.SelectContainer>
      </S.Header>

      <S.DaysGrid>
        {DAY_NAMES_NARROW.map((day) => (
          <S.DayLabel key={day}>{day}</S.DayLabel>
        ))}

        {calendarDays.map((day, index) => (
          <S.DayCell
            key={index}
            type="button"
            data-in-range={day.isInRange ? 'true' : undefined}
            data-selected={day.isSelected ? 'true' : undefined}
            $isCurrentMonth={day.isCurrentMonth}
            $isDisabled={day.isDisabled}
            $isInRange={day.isInRange}
            $isSelected={day.isSelected}
            $isToday={day.isToday}
            onClick={() => handleDateClick(day.date, day.isDisabled)}
          >
            {day.date.getDate()}
          </S.DayCell>
        ))}
      </S.DaysGrid>
    </S.Container>
  )
}
